import { iEmsApiResponse } from "../Logic/TenantAPI";
import { OrganizationRequest, SECRET_NAMES, SecretName, SecretState, TenantItem, TenantRequest } from "./ConfigAPI";

/*
 * The rules both configuration pages share, kept pure so they can be tested
 * without rendering a page. Spec: docs/superpowers/specs/2026-10-22-tenant-org-config-design.md.
 *
 * Form state is one flat map from a field's dotted path ("outbound.port") to
 * the text in its input. "" always means not set. Yes / No / Not set fields
 * hold "true", "false" or "". The paths are the request's own property names,
 * so a server error key maps straight back to its field.
 */

export type FormState = Record<string, string>;
export type FieldErrors = Record<string, string>;
export type FieldKind = "text" | "number" | "flag" | "choice";

export const ORGANIZATION_FIELDS: Record<string, FieldKind> = {
    name: "text",
    enableCloudMailProcessing: "flag",
    autoResponseMaxPerWindow: "number",
    autoResponseWindowMinutes: "number",
    autoResponseBodySizeToleranceBytes: "number",
    "inbound.mailProvider": "choice",
    "inbound.server": "text",
    "inbound.port": "number",
    "inbound.username": "text",
    "inbound.msalTenantId": "text",
    "inbound.msalClientId": "text",
    "outbound.mailProvider": "choice",
    "outbound.server": "text",
    "outbound.port": "number",
    "outbound.username": "text",
    "outbound.authenticationMethods": "choice",
    "outbound.msalTenantId": "text",
    "outbound.msalClientId": "text",
};

export const TENANT_FIELDS: Record<string, FieldKind> = {
    name: "text",
    organization: "choice",
    disabled: "flag",
    theme: "choice",
    enableCloudMailProcessing: "flag",
    autoResponseMaxPerWindow: "number",
    autoResponseWindowMinutes: "number",
    autoResponseBodySizeToleranceBytes: "number",
    connection: "choice",
    database: "text",
    useRdsProxy: "flag",
    s3CachePrefix: "text",
    sqsQueueUrl: "text",
    sqsRegion: "text",
};

/** What the user typed into each secret. "" means untouched. */
export type SecretInputs = Record<SecretName, string>;

export const EMPTY_SECRETS: SecretInputs = {
    inboundPassword: "",
    inboundMsalClientSecret: "",
    outboundPassword: "",
    outboundMsalClientSecret: "",
};

export function readPath(source: object | undefined, path: string): unknown {
    return path
        .split(".")
        .reduce<unknown>((value, key) => (value as Record<string, unknown> | undefined)?.[key], source);
}

function writePath(target: Record<string, unknown>, path: string, value: unknown) {
    const keys = path.split(".");
    let node = target;
    for (const key of keys.slice(0, -1)) {
        node[key] = (node[key] as Record<string, unknown> | undefined) ?? {};
        node = node[key] as Record<string, unknown>;
    }
    node[keys[keys.length - 1]] = value;
}

/**
 * Builds the form from one layer. Pass `stored` to edit, or `effective` to
 * copy what the host uses into the form. A missing property is null on the
 * wire (the API omits nulls), so it becomes "", the same as not set.
 */
export function formFromSettings(fields: Record<string, FieldKind>, settings: object | undefined): FormState {
    const form: FormState = {};
    for (const path of Object.keys(fields)) {
        const value = readPath(settings, path);
        form[path] = value === undefined || value === null ? "" : String(value);
    }
    return form;
}

const WHOLE_NUMBER = /^\d+$/;

/**
 * Turns the form into a request body. Every field is sent, because PUT
 * replaces the whole stored row and a field left out would be cleared. A
 * blank field is sent as null, which clears that override.
 */
export function bodyFromForm(
    fields: Record<string, FieldKind>,
    form: FormState,
): { body: Record<string, unknown>; errors: FieldErrors } {
    const body: Record<string, unknown> = {};
    const errors: FieldErrors = {};
    for (const [path, kind] of Object.entries(fields)) {
        const raw = (form[path] ?? "").trim();
        let value: unknown = null;
        if (raw !== "") {
            if (kind === "number") {
                if (WHOLE_NUMBER.test(raw)) {
                    value = Number(raw);
                } else {
                    errors[path] = "Must be a whole number of 0 or more.";
                }
            } else if (kind === "flag") {
                value = raw === "true";
            } else {
                value = raw;
            }
        }
        writePath(body, path, value);
    }
    return { body, errors };
}

/**
 * Whether a value has the shape of `secrets.*.masked`: it starts with "*" and
 * is only "*" apart from its last 2 characters. The API rejects such a value
 * with a 400 unless it is exactly the current mask, so it is stopped here.
 */
export function looksMasked(value: string): boolean {
    return value.startsWith("*") && /^\**$/.test(value.slice(0, -2));
}

export function organizationRequest(
    form: FormState,
    secrets: SecretInputs,
): { body: OrganizationRequest; errors: FieldErrors } {
    const { body, errors } = bodyFromForm(ORGANIZATION_FIELDS, form);
    for (const name of SECRET_NAMES) {
        // A null secret keeps the stored one. Removing a secret is its own DELETE.
        const value = secrets[name].trim() === "" ? null : secrets[name];
        if (value !== null && looksMasked(value)) {
            errors[name] = "Type the full new secret, or leave it blank to keep the current one.";
        }
        body[name] = value;
    }
    return { body: body as unknown as OrganizationRequest, errors };
}

export function tenantRequest(form: FormState): { body: TenantRequest; errors: FieldErrors } {
    const { body, errors } = bodyFromForm(TENANT_FIELDS, form);
    return { body: body as unknown as TenantRequest, errors };
}

/** "Outbound.Port" → "outbound.port". Keys are PascalCase property names, optionally prefixed "$.". */
export function fieldIdFromErrorKey(key: string): string {
    return key
        .replace(/^\$\./, "")
        .split(".")
        .map(part => part.charAt(0).toLowerCase() + part.slice(1))
        .join(".");
}

/**
 * The field errors of a 400. `CallEmsApi` stores the whole
 * ValidationProblemDetails in `validationErrors`, so its messages are under
 * `.errors`, as the other pages read them.
 */
export function fieldErrorsFromResponse(response: iEmsApiResponse): FieldErrors {
    const details = response.validationErrors as unknown as { errors?: Record<string, string[]> } | undefined;
    const errors: FieldErrors = {};
    for (const [key, messages] of Object.entries(details?.errors ?? {})) {
        errors[fieldIdFromErrorKey(key)] = messages.join(" ");
    }
    return errors;
}

/** One line per error, for the alert. A key with no label (no visible field) is shown as it came. */
export function errorSummary(errors: FieldErrors, labels: Record<string, string>): string {
    return Object.entries(errors)
        .map(([field, message]) => `${labels[field] ?? field}: ${message}`)
        .join("\n");
}

export function failureMessage(status: number): string {
    if (status === 0) {
        return "Could not reach the API.";
    }
    if (status === 401) {
        return "Your session has expired. Sign in again.";
    }
    if (status === 403) {
        return "You need sys-admin rights for configuration.";
    }
    return `The request failed (HTTP ${status}).`;
}

/**
 * One text for every delete. The API cannot yet say whether appsettings also
 * defines a code (back-end question 5), so the confirm names both outcomes.
 */
export function deleteConfirmText(code: string): string {
    return (
        `Delete the database values for ${code}? If appsettings also defines this code, its appsettings ` +
        "values apply again; otherwise it disappears from every host."
    );
}

const CODE_PATTERN = /^[A-Za-z0-9_-]{1,100}$/;

/**
 * Why a new code cannot be created, or "". PUT is an upsert, so creating a
 * code that already exists would overwrite that row instead of failing.
 */
export function codeProblem(code: string, existingCodes: string[]): string {
    if (!CODE_PATTERN.test(code)) {
        return "Use 1 to 100 letters, digits, _ or -.";
    }
    if (existingCodes.some(existing => existing.toLowerCase() === code.toLowerCase())) {
        return `${code} already exists. Select it in the list to edit it.`;
    }
    return "";
}

const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * `modifiedAt` is UTC but has no "Z". Unlike the mail-processing timestamps
 * (parseEmsDate, which reads offset-less values as SAST), it must be read as UTC.
 */
export function parseUtcDate(value: string | undefined): Date | undefined {
    if (!value) {
        return undefined;
    }
    const parsed = new Date(HAS_OFFSET.test(value) ? value : value + "Z");
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** The date and time in the browser's locale and time zone, then " · " and who saved it, or "" with no row. */
export function formatModified(settings: { modifiedAt?: string; modifiedBy?: string } | undefined): string {
    const at = parseUtcDate(settings?.modifiedAt);
    if (!at) {
        return "";
    }
    const when = at.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
    return settings?.modifiedBy ? `${when} · ${settings.modifiedBy}` : when;
}

/** A secret input's placeholder. The mask is never the input's value. */
export function secretPlaceholder(state: SecretState | undefined): string {
    if (!state?.isSet) {
        return "";
    }
    return state.masked ?? "set (cannot display)";
}

export interface Option {
    label: string;
    value: string;
}

export const NOT_SET: Option = { label: "Not set", value: "" };

export const FLAG_OPTIONS: Option[] = [NOT_SET, { label: "Yes", value: "true" }, { label: "No", value: "false" }];

/** "Authenticate" is the default and is sent as null. Only "none" changes anything. */
export const AUTHENTICATION_OPTIONS: Option[] = [
    { label: "Authenticate (default)", value: "" },
    { label: "None (relay)", value: "none" },
];

/**
 * Not set, then the values. A stored value that is no longer in the list is
 * still offered, so the page shows it rather than a blank. A value that
 * differs only in case keeps its stored spelling, because Dropdown matches its
 * value exactly.
 */
export function choiceOptions(values: readonly string[], current: string): Option[] {
    const options = [NOT_SET];
    let found = current === "";
    for (const value of values) {
        const same = value.toLowerCase() === current.toLowerCase();
        found = found || same;
        options.push({ label: value, value: same ? current : value });
    }
    if (!found) {
        options.push({ label: `${current} (not found)`, value: current });
    }
    return options;
}

/**
 * Which provider-specific group shows for a provider. Office365 and Graph use
 * the app registration (MSAL). Basic and Gmail use the authentication setting.
 * With no provider set the host infers one from the server name, so everything
 * shows. A hidden field keeps its value and is still sent.
 */
export function providerShows(group: "msal" | "authentication", provider: string): boolean {
    if (provider === "") {
        return true;
    }
    if (group === "msal") {
        return provider === "Office365" || provider === "Graph";
    }
    return provider === "Basic" || provider === "Gmail";
}

/** Office365 and Graph use a username and password only when both are set. */
export function credentialsOptional(provider: string): boolean {
    return provider === "Office365" || provider === "Graph";
}

interface Named {
    code: string;
    stored?: { name?: string };
    effective?: { name?: string };
}

export function displayName(item: Named): string {
    return item.stored?.name ?? item.effective?.name ?? "";
}

export function matchesText(item: Named, text: string): boolean {
    const wanted = text.trim().toLowerCase();
    return (
        wanted === "" || item.code.toLowerCase().includes(wanted) || displayName(item).toLowerCase().includes(wanted)
    );
}

/** The organization filter's value for tenants that have none. */
export const NO_ORGANIZATION = "(none)";

/** Distinct organizations of the tenants, ignoring case (the first spelling seen wins), sorted. */
export function organizationsOf(tenants: TenantItem[]): string[] {
    const seen = new Map<string, string>();
    for (const organization of tenants.map(tenantOrganization)) {
        if (organization !== "" && !seen.has(organization.toLowerCase())) {
            seen.set(organization.toLowerCase(), organization);
        }
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** The organization the host uses, else the stored one, else "". */
export function tenantOrganization(item: TenantItem): string {
    return item.effective?.organization ?? item.stored?.organization ?? "";
}

/**
 * The tenant list as shown: filtered by text and by organization ("" is all,
 * NO_ORGANIZATION is tenants without one), sorted by organization then code,
 * case-insensitively, with tenants without an organization last.
 */
export function tenantRows(items: TenantItem[], text: string, organization: string): TenantItem[] {
    const compare = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });
    return items
        .filter(item => matchesText(item, text))
        .filter(item => {
            const own = tenantOrganization(item);
            if (organization === "") {
                return true;
            }
            if (organization === NO_ORGANIZATION) {
                return own === "";
            }
            return own.toLowerCase() === organization.toLowerCase();
        })
        .sort((a, b) => {
            const orgA = tenantOrganization(a);
            const orgB = tenantOrganization(b);
            if ((orgA === "") !== (orgB === "")) {
                return orgA === "" ? 1 : -1;
            }
            return compare(orgA, orgB) || compare(a.code, b.code);
        });
}
